import React, { useState } from "react";
import { Platform, TextInput, StyleSheet, type TextInputProps } from "react-native";
import { useTheme } from "@/hooks/useTheme";

interface NumericInputProps extends Omit<TextInputProps, "onChangeText" | "value"> {
  value: number;
  onChangeValue: (value: number) => void;
}

export function NumericInput({ value, onChangeValue, style, ...rest }: NumericInputProps) {
  const { colors } = useTheme();
  // Keep raw text so the user can type "-", "3.", "-.5" etc. without it snapping
  const [raw, setRaw] = useState(value !== 0 ? String(value) : "");

  const handleChangeText = (v: string) => {
    // Allow empty, lone minus, trailing decimal, leading "-.""
    if (v === "" || v === "-" || v === "." || v === "-.") {
      setRaw(v);
      if (v === "" || v === "-" || v === "." || v === "-.") onChangeValue(0);
      return;
    }
    // Allow intermediate states like "3." or "-0."
    if (/^-?\d*\.?\d*$/.test(v)) {
      setRaw(v);
      const num = parseFloat(v);
      if (!isNaN(num)) onChangeValue(num);
    }
  };

  return (
    <TextInput
      keyboardType="decimal-pad"
      value={raw}
      placeholder="0.00"
      placeholderTextColor={colors.placeholder}
      onChangeText={handleChangeText}
      style={[styles.input, { color: colors.text, borderColor: colors.border }, style]}
      {...rest}
    />
  );
}

const styles = StyleSheet.create({
  input: {
    height: 40,
    padding: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    fontSize: 16,
    backgroundColor: "transparent",
    ...(Platform.OS === "web" ? { outlineStyle: "none" as const } : {}),
  },
});
